import lodash from 'lodash'
import { useMemo } from 'react'
import { useSearchParams } from 'react-router'
import { useUserState } from '~/atoms/UserState'
import { FilterButton } from '~/components/shared/FilterButton'
import {
  DirectionItem,
  OutcomeItem,
  tryGetFilterDataFromQueryParams,
} from '~/pages/Calls/data/CallFilterData'
import { useGetPbxUsers } from '~/queries/userQueries'
import { FilterRow, FilterSection, SelectedFilterData } from '~/types/Shared'

import { CallsRouteQueryParametersRaw } from './CallsRoutingTypes'

const getOutcomeSection = (
  directionSectionItems: DirectionItem[],
  currentOutcomeSectionItems: OutcomeItem[],
) => {
  const selectedDirectionItems = directionSectionItems.filter(
    (item) => item.isSelected,
  )

  const outcomeSectionItems: OutcomeItem[] = lodash.cloneDeep(
    currentOutcomeSectionItems,
  )

  switch (selectedDirectionItems.length) {
    case 1: {
      const sectionItem = selectedDirectionItems[0]
      const isOnlyOutboundSelected = sectionItem.title === 'Outbound'
      if (!isOnlyOutboundSelected) {
        break
      }

      // Disable only missed/answered, reset selection state
      for (let i = 0; i < 2; i++) {
        const item = outcomeSectionItems[i]
        item.isDisabled = true
        item.isSelected = false
      }
      break
    }
    default: {
      // Enable all items
      outcomeSectionItems.forEach((item) => {
        item.isDisabled = false
      })
      break
    }
  }

  return {
    title: 'Outcome',
    rows: [
      {
        items: outcomeSectionItems,
      },
    ] as FilterRow<OutcomeItem>[],
  }
}
const PBX_USERS_LIMIT = 999
const PBX_USERS_STALE_TIME = 1000 * 60 * 5 //5 minutes
interface CallsFilterButtonProps {
  onActiveFiltersChanged?: (activeFilters: SelectedFilterData[]) => void
  useQuickActionButton?: boolean
}

const getActiveFilterDataFromSections = (filterSections: FilterSection[]) => {
  const newActiveFilterData: SelectedFilterData[] = []

  filterSections.forEach((section) => {
    const sectionTitle = section.title
    section.rows.forEach((row) => {
      const rowTitle = row.title
      row.items.forEach((item) => {
        if (item.isSelected) {
          newActiveFilterData.push({
            sectionTitle,
            rowTitle,
            itemTitle: item.title,
            item,
          })
        }
      })
    })
  })

  return newActiveFilterData
}

const getActiveFiltersCount = (filterSections: FilterSection[]) => {
  return filterSections.reduce((acc, section) => {
    return (
      acc +
      section.rows.reduce((rowAcc, row) => {
        return rowAcc + row.items.filter((item) => item.isSelected).length
      }, 0)
    )
  }, 0)
}

export const CallsFilterButton = ({
  onActiveFiltersChanged,
  useQuickActionButton,
}: CallsFilterButtonProps) => {
  const [searchParams] = useSearchParams()
  const { pbxId, callAnalyticsEnabled } = useUserState()

  const { data, isLoading } = useGetPbxUsers({
    pbxId: pbxId ?? 0,
    options: {
      staleTime: PBX_USERS_STALE_TIME,
    },
    _queryParams: {
      limit: PBX_USERS_LIMIT,
    },
  })

  const filterOptions = useMemo(() => {
    const queryParams = Object.fromEntries(
      searchParams,
    ) as CallsRouteQueryParametersRaw
    const { filterData } = tryGetFilterDataFromQueryParams({
      callsQueryParams: queryParams,
      callAnalyticsEnabled,
      pbxUsers: data?.results ?? [],
    })
    return filterData
  }, [searchParams, callAnalyticsEnabled, data, isLoading])

  const activeFilters = useMemo(() => {
    return getActiveFiltersCount(filterOptions)
  }, [filterOptions])
  const handleOnActiveFiltersApplied = (updatedFilterData: FilterSection[]) => {
    const directionSection = updatedFilterData.find(
      (section) => section.title === 'Direction',
    )
    const outcomeSection = updatedFilterData.find(
      (section) => section.title === 'Outcome',
    )

    if (directionSection && outcomeSection) {
      const newOutcomeSection = getOutcomeSection(
        directionSection.rows[0].items as DirectionItem[],
        outcomeSection.rows[0].items as OutcomeItem[],
      )

      const outcomeSectionIndex = updatedFilterData.findIndex(
        (sec) => sec.title === 'Outcome',
      )

      if (outcomeSectionIndex !== -1) {
        updatedFilterData[outcomeSectionIndex] = newOutcomeSection
      }
    }
    const newActiveFilterData =
      getActiveFilterDataFromSections(updatedFilterData)
    onActiveFiltersChanged?.(newActiveFilterData)
  }

  return (
    <FilterButton
      filterData={filterOptions}
      title="Filter"
      activeFiltersCount={activeFilters ?? 0}
      onFilterChangesApplied={handleOnActiveFiltersApplied}
      useQuickActionButton={useQuickActionButton}
    />
  )
}
